import React, { type ComponentType, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type {
	AvailabilityContext,
	HiddenInConfigurationWithIssueTypes,
} from '../../common/types/layout-items.tsx';
import { FieldConfigurationDefaultValue } from '../../common/ui/field-active/styled.tsx';
import { NewTabLink } from '../../common/ui/new-tab-link/index.tsx';
import { NO_CONTEXT } from './constants.tsx';
import { messages } from './messages.tsx';

type HiddenInContextWarningProps = Readonly<{
	context: AvailabilityContext | typeof NO_CONTEXT;
	customFieldId: string;
	intl: IntlShape;
	showGoToContextLink: boolean;
}>;

const HiddenInContextWarning = ({
	context,
	customFieldId,
	intl,
	showGoToContextLink,
}: HiddenInContextWarningProps) => {
	if (!showGoToContextLink && fg('dagc-53-hide-go-to-context-link-for-da')) {
		return (
			<SectionMessage appearance="warning">
				{intl.formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.contextNotAppliedNonGlobalAdminIssueTermRefresh
						: messages.contextNotAppliedNonGlobalAdmin,
				)}
			</SectionMessage>
		);
	}

	const link =
		context === NO_CONTEXT
			? `/secure/admin/ConfigureCustomField!default.jspa?customFieldId=${customFieldId}`
			: `/secure/admin/ManageConfigurationScheme!default.jspa?customFieldId=${customFieldId}&fieldConfigSchemeId=${context.id}`;

	return (
		<SectionMessage appearance="warning">
			{intl.formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.contextNotAppliedIssueTermRefresh
					: messages.contextNotApplied,
				{
					a: (message: string[]): ReactNode => <NewTabLink href={link}>{message}</NewTabLink>,
				},
			)}
		</SectionMessage>
	);
};

type HiddenInConfigurationWarningsProps = Readonly<{
	hiddenInConfigurationWithIssueTypes: HiddenInConfigurationWithIssueTypes[];
	intl: IntlShape;
	showGoToFieldConfigurationLink: boolean;
}>;

export const HiddenInConfigurationWarnings = ({
	intl,
	hiddenInConfigurationWithIssueTypes,
	showGoToFieldConfigurationLink,
}: HiddenInConfigurationWarningsProps) => (
	<>
		{hiddenInConfigurationWithIssueTypes.map((e, i) => {
			const { fieldConfigId, fieldConfigName, fieldConfigIssueTypes, isDefaultFieldConfig } = e;
			const associatedIssueTypesCount = fieldConfigIssueTypes.length;
			if (
				!showGoToFieldConfigurationLink &&
				fg('dagc-157-hide-go-to-field-configuration-link-da')
			) {
				if (isDefaultFieldConfig) {
					return (
						<SectionMessage key={fieldConfigId + i} appearance="warning">
							{intl.formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.hiddenInConfigurationForNIssueTypesNonGlobalAdminIssueTermRefresh
									: messages.hiddenInConfigurationForNIssueTypesNonGlobalAdmin,
								{
									associatedIssueTypesCount,
								},
							)}
						</SectionMessage>
					);
				}

				if (associatedIssueTypesCount === 1) {
					return (
						<SectionMessage key={fieldConfigId + i} appearance="warning">
							{intl.formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.hiddenInConfigurationForSingleIssueTypeNonGlobalAdminIssueTermRefresh
									: messages.hiddenInConfigurationForSingleIssueTypeNonGlobalAdmin,
								{
									issueTypeName: fieldConfigIssueTypes[0],
								},
							)}
						</SectionMessage>
					);
				}
				if (associatedIssueTypesCount === 2) {
					return (
						<SectionMessage key={fieldConfigId + i} appearance="warning">
							{intl.formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.hiddenInConfigurationForTwoIssueTypesNonGlobalAdminIssueTermRefresh
									: messages.hiddenInConfigurationForTwoIssueTypesNonGlobalAdmin,
								{
									issueTypeName1: fieldConfigIssueTypes[0],
									issueTypeName2: fieldConfigIssueTypes[1],
								},
							)}
						</SectionMessage>
					);
				}
				return (
					<SectionMessage key={fieldConfigId + i} appearance="warning">
						{intl.formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.hiddenInConfigurationForNIssueTypesNonGlobalAdminIssueTermRefresh
								: messages.hiddenInConfigurationForNIssueTypesNonGlobalAdmin,
							{
								associatedIssueTypesCount,
							},
						)}
					</SectionMessage>
				);
			}
			if (isDefaultFieldConfig) {
				return (
					<SectionMessage key={fieldConfigId + i} appearance="warning">
						{intl.formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.hiddenInConfigurationForNIssueTypesIssueTermRefresh
								: messages.hiddenInConfigurationForNIssueTypes,
							{
								associatedIssueTypesCount,
								fieldConfigName,
								a: (message: string[]): ReactNode => (
									<NewTabLink
										href={`/secure/admin/ConfigureFieldLayout!default.jspa?id=${fieldConfigId}`}
									>
										{message}
									</NewTabLink>
								),
							},
						)}
					</SectionMessage>
				);
			}

			if (associatedIssueTypesCount === 1) {
				return (
					<SectionMessage key={fieldConfigId + i} appearance="warning">
						{intl.formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.hiddenInConfigurationForSingleIssueTypeIssueTermRefresh
								: messages.hiddenInConfigurationForSingleIssueType,
							{
								issueTypeName: fieldConfigIssueTypes[0],
								fieldConfigName,
								a: (message: string[]): ReactNode => (
									<NewTabLink
										href={`/secure/admin/ConfigureFieldLayout!default.jspa?id=${fieldConfigId}`}
									>
										{message}
									</NewTabLink>
								),
							},
						)}
					</SectionMessage>
				);
			}
			if (associatedIssueTypesCount === 2) {
				return (
					<SectionMessage key={fieldConfigId + i} appearance="warning">
						{intl.formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.hiddenInConfigurationForTwoIssueTypesIssueTermRefresh
								: messages.hiddenInConfigurationForTwoIssueTypes,
							{
								issueTypeName1: fieldConfigIssueTypes[0],
								issueTypeName2: fieldConfigIssueTypes[1],
								fieldConfigName,
								a: (message: string[]): ReactNode => (
									<NewTabLink
										href={`/secure/admin/ConfigureFieldLayout!default.jspa?id=${fieldConfigId}`}
									>
										{message}
									</NewTabLink>
								),
							},
						)}
					</SectionMessage>
				);
			}
			return (
				<SectionMessage key={fieldConfigId + i} appearance="warning">
					{intl.formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.hiddenInConfigurationForNIssueTypesIssueTermRefresh
							: messages.hiddenInConfigurationForNIssueTypes,
						{
							associatedIssueTypesCount,
							fieldConfigName,
							a: (message: string[]): ReactNode => (
								<NewTabLink
									href={`/secure/admin/ConfigureFieldLayout!default.jspa?id=${fieldConfigId}`}
								>
									{message}
								</NewTabLink>
							),
						},
					)}
				</SectionMessage>
			);
		})}
	</>
);

type Props = Readonly<{
	fieldKey: string;
	hiddenInContext: AvailabilityContext | typeof NO_CONTEXT | null;
	hiddenInConfigurationsWithIssueTypes: HiddenInConfigurationWithIssueTypes[] | null;
	intl: IntlShape;
	HiddenInContextComponent?: ComponentType<HiddenInContextWarningProps>;
}>;

export const RestrictedFieldWarnings = ({
	hiddenInContext,
	hiddenInConfigurationsWithIssueTypes,
	fieldKey,
	intl,
	HiddenInContextComponent: HiddenInContextComponentFromProps,
}: Props) => {
	const customFieldId = fieldKey.split('_')[1];
	const isGlobalAdmin = useIsAdmin();

	const HiddenInContextWarningToBeUsed =
		HiddenInContextComponentFromProps || HiddenInContextWarning;
	return (
		<RestrictedFieldDetails>
			{hiddenInContext !== null && (
				<HiddenInContextWarningToBeUsed
					context={hiddenInContext}
					customFieldId={customFieldId}
					intl={intl}
					showGoToContextLink={isGlobalAdmin}
				/>
			)}
			{hiddenInConfigurationsWithIssueTypes && (
				<HiddenInConfigurationWarnings
					hiddenInConfigurationWithIssueTypes={hiddenInConfigurationsWithIssueTypes}
					intl={intl}
					showGoToFieldConfigurationLink={isGlobalAdmin}
				/>
			)}
		</RestrictedFieldDetails>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RestrictedFieldDetails = styled(FieldConfigurationDefaultValue)({
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> *:not(:last-child)': {
		marginBottom: token('space.200'),
	},
});
